import React from 'react';
import { Col, Row, Tabs, Typography } from 'antd';
import { ApiOutlined, CloudServerOutlined, ClockCircleOutlined, ToolOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import WorkersCard from '../components/WorkersCard';
import useSettings from '../hooks/useSettings';
import {
  CheckIntervalCard, CheckDistributionCard, ConcurrencyCard, StaleTargetsCard,
} from '../components/settings/CheckingCards';
import ProxyCheckerCard from '../components/settings/ProxyCheckerCard';
import MobileGatewayCard from '../components/settings/MobileGatewayCard';
import { TicketSystemCard, ReportsCard, DebugDumpCard } from '../components/settings/SystemCards';
import BackupRestoreCard from '../components/settings/BackupRestoreCard';
import ResetDataCard from '../components/settings/ResetDataCard';

const { Title } = Typography;

const TAB_KEYS = ['workers', 'proxy', 'checking', 'system'] as const;
type TabKey = typeof TAB_KEYS[number];

// Eleven cards stacked were ~2900px — four screens of scrolling to find one
// setting. Grouped by what the operator came to change, each tab fits in
// about one screen. The tab lives in the URL hash so a link (or a reload)
// lands on the same group.
const SettingsPage: React.FC = () => {
  const s = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const fromHash = location.hash.replace('#', '') as TabKey;
  const active: TabKey = TAB_KEYS.includes(fromHash) ? fromHash : 'workers';

  const items = [
    {
      key: 'workers',
      label: <span><CloudServerOutlined /> Workers</span>,
      children: <WorkersCard />,
    },
    {
      key: 'proxy',
      label: <span><ApiOutlined /> Proxy checker</span>,
      children: <ProxyCheckerCard s={s} />,
    },
    {
      key: 'checking',
      label: <span><ClockCircleOutlined /> Scheduling</span>,
      children: (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <CheckIntervalCard s={s} />
            <CheckDistributionCard s={s} />
            <ConcurrencyCard s={s} />
          </Col>
          <Col xs={24} lg={12}>
            <MobileGatewayCard s={s} />
            <StaleTargetsCard s={s} />
          </Col>
        </Row>
      ),
    },
    {
      key: 'system',
      label: <span><ToolOutlined /> System</span>,
      children: (
        <Row gutter={16}>
          <Col xs={24} lg={12}>
            <TicketSystemCard s={s} />
            <ReportsCard s={s} />
            <DebugDumpCard s={s} />
          </Col>
          <Col xs={24} lg={12}>
            <BackupRestoreCard s={s} />
            <ResetDataCard s={s} />
          </Col>
        </Row>
      ),
    },
  ];

  return (
    <div>
      <Title level={4} style={{ margin: '0 0 4px' }}>Settings</Title>
      <Tabs
        activeKey={active}
        onChange={(key) => navigate({ hash: key }, { replace: true })}
        items={items}
        size="small"
        tabBarStyle={{ marginBottom: 12 }}
      />
    </div>
  );
};

export default SettingsPage;
